/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useMemo } from "react";
import { sanitizeHtml } from "../../utils/sanitize";
import TextField from "../ui/form/TextField";
import Checkbox from "../ui/form/Checkbox";
import FormGroup from "../ui/form/FormGroup";
import FormError from "../ui/form/FormError";
import PasswordInput from "../ui/form/PasswordInput";
import PhoneField from "../ui/form/PhoneField";
import Select from "../ui/Select";
import DateField from "../ui/form/DateField";
import NumberField from "../ui/form/NumberField";
import TextareaField from "../ui/form/TextareaField";
import EmailField from "../ui/form/EmailField";
import { sanitizeFormValues } from "../../utils/helpers/sanitizeValues";
import { CLIENT_PROMO_UNSUBSCRIBE_KEYS } from "../../constants/constants";
import useCountriesAndStates from "../../hooks/useCountriesAndStates";
import LoadingIndicator from "../LoadingIndicator";
import { useTranslate } from "../../hooks/useTranslate";
import { useConfig } from "../../lib/react-query/useConfig";
import { useCompany } from "../../lib/react-query/useCompany";
import { useAuth } from "../../hooks/useAuth";

const ClientFieldsGroup = ({ form, fields, hasClientData = false, readOnly = false, isRegisterForm = false }) => {
  const {
    control,
    formState: { errors },
  } = form;
  const { data: config } = useConfig();
  const { data: company } = useCompany();
  const { _t } = useTranslate();
  const { client } = useAuth();
  const { formattedCountriesOptions, formattedStatesOptions, handleCountryChange, isLoading, countryHasStates } =
    useCountriesAndStates(!!config.client_login_allow_address);

  const fieldComponents = useMemo(
    () => ({
      text: TextField,
      email: EmailField,
      checkbox: Checkbox,
      select: Select,
      digits: NumberField,
      phone: PhoneField,
      country: Select,
      state: Select,
      address1: TextField,
      address2: TextField,
      city: TextField,
      zip: TextField,
      date: DateField,
      textarea: TextareaField,
      static: ({ field }) => <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.default_value) }} />,
      password: PasswordInput,
    }),
    []
  );

  const sanitizedFields = useMemo(
    () =>
      fields.map((field) => ({
        ...field,
        value: sanitizeFormValues(
          hasClientData ? (field.is_built_in ? client[field.id] : client.client_fields?.[field.id]) : field.value
        ),
      })),
    [fields, client, hasClientData]
  );

  const formattedSelectOptions = useCallback(
    (options) =>
      options.map((option) => ({
        value: option.value,
        label: option.value,
      })),
    []
  );

  if (isLoading) {
    return <LoadingIndicator page />;
  }

  const renderField = (field) => {
    if ((field.id === "password" && !isRegisterForm) || (field.id === "state_id" && !countryHasStates)) {
      return null;
    }

    const FieldComponent = fieldComponents[field.type];

    if (!FieldComponent) {
      console.warn(`Unsupported field type: ${field.type}`);
      return null;
    }

    const commonProps = {
      control,
      name: field.is_built_in ? field.id : `client_fields.${field.id}`,
      defaultValue: !hasClientData ? field.default_value : field.value,
      label: field.is_built_in ? _t(field.id) : field.title,
      placeholder: field.is_built_in ? _t(field.id) : field.title,
      required: !field.is_optional,
      disabled: readOnly,
      readOnly,
    };

    if (field.type === "static") {
      return <FieldComponent key={field.id} field={field} />;
    }

    const fieldSpecificProps = {
      select: {
        defaultValue: !!field.value ? field.value : field.values.find((value) => value.is_default)?.value,
        options: formattedSelectOptions(field.values),
      },
      country: {
        options: formattedCountriesOptions,
        onChange: handleCountryChange,
      },
      state: {
        options: formattedStatesOptions,
      },
      phone: {
        prefixChangable: config.country_prefix_is_changeable,
        country: (config.country_prefix_id || config.country_phone_initial || company?.country_id)?.toLowerCase(),
      },
    };

    return (
      <FormGroup key={field.id}>
        <FieldComponent {...commonProps} {...(fieldSpecificProps[field.type] || {})} />
        {field.is_built_in
          ? errors[field.id] && <FormError name={field.id}>{errors[field.id].message}</FormError>
          : errors?.client_fields?.[field.id] && <FormError name={`client_fields.${field.id}`}>{errors.client_fields[field.id].message}</FormError>}
      </FormGroup>
    );
  };

  return (
    <>
      {sanitizedFields?.map(renderField)}
      {hasClientData && (
        <>
          <FormGroup>
            <Checkbox
              control={control}
              name={CLIENT_PROMO_UNSUBSCRIBE_KEYS.SMS}
              label="is_sms_unsubscribed"
              defaultValue={client[CLIENT_PROMO_UNSUBSCRIBE_KEYS.SMS]}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup>
            <Checkbox
              control={control}
              name={CLIENT_PROMO_UNSUBSCRIBE_KEYS.EMAIL}
              label="is_email_unsubscribed"
              defaultValue={client[CLIENT_PROMO_UNSUBSCRIBE_KEYS.EMAIL]}
              disabled={readOnly}
            />
          </FormGroup>
        </>
      )}
    </>
  );
};

export default ClientFieldsGroup;
